import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { useCanPlaceOrders } from '@app/hooks';
import { fetchHistoricalUsageInfo, selectHistoricalUsageInfo } from '@app/redux/usageHistory';
import { selectUserInfo } from '@app/redux/userInfo';

import { BillCycleBarChart } from '../../components/BarCharts';
import { BillCycleDropdown } from '../../components/BillCycleDropdown';
import { ChartLoading } from '../../components/ChartLoading';
import { DisconnectedState } from '../../components/DisconnectedState';
import { ErrorState } from '../../components/ErrorState';
import { PreInstallState } from '../../components/PreInstallState';
import { useStoreContext } from '../../context';
import { Header } from '../Header';

import LoadingStyles from '../../components/Loading/Loading.module.scss';
import Styles from './DailyUsageChart.module.scss';

// For non-unleashed plans
export const DailyUsageChart = (): JSX.Element | null => {
  const { t } = useTranslation('UsageHistory');
  const [chartReloading, setChartReloading] = useState(false);
  const { selectedBillCycle } = useStoreContext();

  const {
    userInfo: { isPreInstall },
  } = useSelector(selectUserInfo);

  const { isDisconnected } = useCanPlaceOrders();
  const { loading: isLoading, errors: hasApiErrors } = useSelector(selectHistoricalUsageInfo);

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(fetchHistoricalUsageInfo());
  }, []);

  useEffect(() => {
    //If bill cycle changes, then unmount and reload the chart, because the chart doesn't refresh for updated scrollPositionX
    setChartReloading(true);
    setTimeout(() => {
      setChartReloading(false);
    }, 0);
  }, [selectedBillCycle]);

  if (isPreInstall) {
    return (
      <div className={Styles['UsageChartWrapper']} data-cy="usage-chart-wrapper">
        <div className={Styles.TitleSection}>
          <Txt p="24px" color="regular" variant="heading5">
            {t('chartTitle')}
          </Txt>
        </div>
        <PreInstallState />
      </div>
    );
  }

  if (isDisconnected) {
    return (
      <div data-cy={'usage-chart-wrapper'} className={Styles['UsageChartWrapper']}>
        <div className={Styles.TitleSection}>
          <Txt p="24px" color="regular" variant="heading5">
            {t('chartTitle')}
          </Txt>
        </div>
        <DisconnectedState />
      </div>
    );
  }

  return (
    <div className={Styles['UsageChartWrapper']} data-cy="usage-chart-wrapper">
      <div className={Styles.TitleSection}>
        <Txt p="24px" color="regular" variant={'heading5'}>
          {t('chartTitle')}
        </Txt>
        {!hasApiErrors && (
          <div className={Styles.BillCycleDropdown}>
            {isLoading ? (
              <div className={LoadingStyles.billCycleNavigatorLoading} data-cy="loading-billcycle" />
            ) : (
              <BillCycleDropdown />
            )}
          </div>
        )}
      </div>
      {hasApiErrors ? (
        <ErrorState />
      ) : (
        <>
          <Header />
          {isLoading || chartReloading ? (
            <div style={{ height: 453, display: 'flex' }}>
              <ChartLoading />
            </div>
          ) : (
            <div className={Styles.UsageChart}>
              <BillCycleBarChart />
            </div>
          )}
        </>
      )}
    </div>
  );
};
